import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Col, Row } from "antd";
import { observer } from "mobx-react";
import { useForm } from "antd/es/form/Form";
import moment from "moment";
import config from "./config";
import Field from "../../common/forms/field";
import Form from "../../common/forms/form";
import { CREATE_BUTTON } from "../../../constants/translates";
import { SAVE_BUTTON } from "../../../constants/buttons";
import CreateDeclarantStore from "../../../stores/CreateDeclarantStore";
import { Option } from "../../common/forms/Interfaces";
import { Declarant } from "../../../models/Declarant";
import {DeepPartial} from "../../../models/Common";

interface Props {
  onFinish: () => void;
  onEdit?: (values: DeepPartial<Declarant>) => void | Promise<void>;
  clients: Option[];
  noClient?: Option;
  departments: Option[];
  edit?: boolean;
  defaultValues?: DeepPartial<Declarant>;
}

const gutter = 15;

const store = new CreateDeclarantStore();

const CreateDeclarant: React.FC<Props> = ({
  onFinish,
  onEdit,
  clients,
  noClient,
  departments,
  edit,
  defaultValues,
}) => {
  const [form] = useForm();

  const normalizedDefaultValues = useMemo(
    () =>
      defaultValues
        ? {
            ...defaultValues,
            creation_date: defaultValues.creation_date
              ? moment(defaultValues.creation_date)
              : undefined,
            date_cmr: defaultValues.date_cmr
              ? moment(defaultValues.date_cmr)
              : undefined,
          }
        : undefined,
    [defaultValues]
  );

  const formConfig = useMemo(
    () => ({
      ...config,
      client_id: {
        ...config.client_id,
        options: clients,
        defaultValue: edit
          ? defaultValues?.client_id ?? noClient?.value
          : noClient?.value,
      },
      department_id: {
        ...config.department_id,
        options: departments,
        defaultValue: edit
          ? defaultValues?.department_id ?? undefined
          : undefined,
      },
    }),
    [clients, departments, defaultValues, edit, noClient]
  );

  useEffect(() => {
    if (edit && normalizedDefaultValues) {
      form.setFieldsValue(normalizedDefaultValues);
      return;
    }

    form.resetFields();
  }, [edit, form, normalizedDefaultValues]);

  const onFinishHandler = useCallback(
    (values: unknown): void => {
      if (edit) {
        Promise.resolve()
          .then(() => onEdit?.(values as Declarant))
          .finally(() => {
            onFinish();
          });
        return;
      }

      store.create(values as object).finally(() => {
        if (Object.keys(store.errors).length > 0) {
          return;
        }

        form.resetFields();
        onFinish();
      });
    },
    [edit, form, onEdit, onFinish]
  );

  return (
    <Form
      config={formConfig}
      errors={store.errors as { [key: string]: string[] | null }}
      onFinishHandler={onFinishHandler}
      form={form}
      initialValues={edit ? normalizedDefaultValues : undefined}
    >
      <Field name="client_id" />
      <Field name="department_id" />
      <Field name="creation_date" />
      <Row gutter={gutter}>
        <Col md={7}>
          <Field name="means_of_transportation" />
        </Col>
        <Col md={7}>
          <Field name="carrier" />
        </Col>
        <Col md={7}>
          <Field name="driver" />
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col span={6}>
          <Field name="consignment_items" />
        </Col>
        <Col span={6}>
          <Field name="invoice" />
        </Col>
        <Col span={6}>
          <Field name="currency" />
        </Col>
        <Col span={6}>
          <Field name="brutto" />
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col span={12}>
          <Field name="cmr" />
        </Col>
        <Col span={12}>
          <Field name="date_cmr" />
        </Col>
      </Row>
      <Field name="consignor" />
      <Field name="border_checkpoint_name" />
      <Row gutter={gutter}>
        <Col span={12}>
          <Field name="customs_code_and_office_name" />
        </Col>
        <Col span={12}>
          <Field name="customs_code" />
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col span={12}>
          <Field name="way" />
        </Col>
        <Col span={12}>
          <Field name="note" />
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col span={12}>
          <Field name="measure_code" />
        </Col>
        <Col span={12}>
          <Field name="warranty" />
        </Col>
      </Row>
      <Button type="primary" htmlType="submit">
        {edit ? SAVE_BUTTON : CREATE_BUTTON}
      </Button>
    </Form>
  );
};

export default observer(CreateDeclarant);
